// The six barrier grids, as data rather than as six components.
//
// THE COLUMN ORDERS BELOW ARE THE SOURCE'S, read out of the AppSheet view
// definitions during the phase 6 sign-off on 2026-09-15 and no longer invented
// here. `#59` said to pull them from the source definition rather than assume
// them; the first build could not, because the document holding them was not
// reachable, and every one of the six was wrong as a result.
//
// Three deliberate departures from what the source shows, each of them a
// decision taken earlier in the programme rather than a judgement made here:
//
//   `Copy`       -- dropped. It is the source's click target for
//                   `Duplicate Barrier`, and that is a button on the row here.
//   `Line`       -- dropped by D7, along with the `Lines` table: set on one row
//                   of 1,824, read-only and hidden in the source.
//   `Escalator`  -- dropped in phase 3: blank on all 1,824 rows, so there is no
//                   column in the target to show. It appears in the source
//                   order for `Department`, `Archive` and nothing else.
//
// And three the users asked for after sign-off: `Make Parts` is off
// `Barriers Dashboard`, as it is off the form; and `Action Owner` then
// `Entered By`, which the source never had, are the last two columns on all
// six, so the signed-off order ahead of them stays where people know it.
// `Action Owner` is there because the filter menu filters on it, and a grid
// narrowed to one person should show why each row is on it.
//
// `tests/barrierViews.test.ts` holds every one of these orders against the
// sign-off report, so a column that moves has to move there too.

import type { BarrierScreenProps } from './BarrierScreen';

/** The order `Department` and `Archive` share -- the source uses one definition
 *  for both, less `Escalator`, which the target does not carry. */
const DEPARTMENT_ORDER = [
  'plant',
  'area',
  'customerName',
  'jobNumber',
  'product',
  'dateOfEscalation',
  'problem',
  'partNumber',
  'week1',
  'week2',
  'makeParts',
  'buyerStatus',
  'warehouseStatus',
  'commodity',
  'correctiveAction',
  'owners',
  'department',
  'actualClose',
  'actionOwner',
  'enteredBy',
];

export const BARRIER_SCREENS: Record<string, BarrierScreenProps> = {
  barriersDashboard: {
    title: 'Barriers Dashboard',
    view: 'open',
    description: 'Open barriers, grouped by action category.',
    // The source's order, less `Make Parts`, which the users took off this
    // screen as they had off the form: Action Type `MAKE` says what it said.
    // The other barrier grids still show it.
    columns: [
      'jobNumber',
      'dateOfEscalation',
      'commodity',
      'problem',
      'partNumber',
      'week1',
      'week2',
      'buyerStatus',
      'warehouseStatus',
      'toNumber',
      'correctiveAction',
      'actionCategory',
      'hot',
      'rootCause',
      'owners',
      'department',
      'actualClose',
      'plant',
      'area',
      'product',
      'actionOwner',
      'enteredBy',
    ],
    groupBy: ['actionCategory'],
    groupDesc: [true],
    sortBy: [{ key: 'commodity' }],
    editable: true,
    rowTap: 'edit',
  },

  hot: {
    title: 'HOT',
    view: 'open-hot',
    description: 'Open barriers flagged hot.',
    // The source's HOT order is its Barriers Dashboard order without
    // `Make Parts`, `Buyer Status` and `Warehouse Status`. The first build had
    // that inverted -- it carried the two statuses here and omitted `Hot`.
    columns: [
      'jobNumber',
      'dateOfEscalation',
      'commodity',
      'problem',
      'partNumber',
      'week1',
      'week2',
      'toNumber',
      'correctiveAction',
      'actionCategory',
      'hot',
      'rootCause',
      'owners',
      'department',
      'actualClose',
      'plant',
      'area',
      'product',
      'actionOwner',
      'enteredBy',
    ],
    groupBy: ['actionCategory'],
    groupDesc: [true],
    sortBy: [{ key: 'commodity' }],
    editable: true,
    rowTap: 'edit',
  },

  plants: {
    title: 'Plants',
    view: 'open',
    description: 'Open barriers by plant.',
    // `Plant` and `Area` sit at positions 13 and 14, not at the front. Leading
    // with the grouping column is the obvious arrangement and not the one the
    // source uses -- the group header already says which plant it is.
    columns: [
      'jobNumber',
      'dateOfEscalation',
      'problem',
      'week1',
      'week2',
      'makeParts',
      'buyerStatus',
      'warehouseStatus',
      'commodity',
      'correctiveAction',
      'owners',
      'actualClose',
      'plant',
      'area',
      'product',
      'department',
      'partNumber',
      'actionOwner',
      'enteredBy',
    ],
    groupBy: ['plant'],
    // The one barrier grid the source does not enable quick edit on, and the
    // one whose row tap opens the record rather than the form.
    editable: false,
    rowTap: 'detail',
  },

  department: {
    title: 'Department',
    view: 'open',
    description: 'Open barriers by owner department, then commodity.',
    columns: DEPARTMENT_ORDER,
    groupBy: ['department', 'commodity'],
    editable: true,
    rowTap: 'detail',
  },

  archive: {
    title: 'Archive',
    view: 'archive',
    description: 'Every barrier, open and closed. The only way a closed barrier is reached.',
    // The source gives `Archive` the same column order as `Department`.
    columns: DEPARTMENT_ORDER,
    groupBy: ['product'],
    editable: true,
    rowTap: 'detail',
  },

  interplantTransfers: {
    title: 'Interplant Transfers',
    view: 'interplant-transfers',
    description: 'Barriers carrying a transfer reference. This is the screen the application opens on.',
    // The `Department` order with `TO Number` inserted after `Part Number`. The
    // first build pulled `TO Number` forward to position 6, which reads as the
    // obvious thing to do on a transfers screen and is not what the source does.
    columns: [
      'plant',
      'area',
      'customerName',
      'jobNumber',
      'product',
      'dateOfEscalation',
      'problem',
      'partNumber',
      'toNumber',
      'week1',
      'week2',
      'makeParts',
      'buyerStatus',
      'warehouseStatus',
      'commodity',
      'correctiveAction',
      'owners',
      'department',
      'actualClose',
      'actionOwner',
      'enteredBy',
    ],
    groupBy: ['product', 'jobNumber'],
    sortBy: [{ key: 'jobNumber' }],
    editable: true,
    rowTap: 'edit',
  },
};
